import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { supabase } from './supabaseClient';

// Constants for PDF styling
const PDF_STYLES = {
  primaryColor: [44, 62, 80], // Darker blue
  secondaryColor: [52, 73, 94], // Lighter blue
  textColor: [44, 62, 80],
  margins: {
    top: 30,
    left: 25,
    right: 25,
    bottom: 25
  },
  fonts: {
    header: {
      size: 24,
      style: 'bold'
    },
    subheader: {
      size: 16,
      style: 'bold'
    },
    body: {
      size: 11,
      style: 'normal'
    }
  },
  table: {
    headerBg: [44, 62, 80],
    headerText: [255, 255, 255],
    stripedBg: [245, 247, 250],
    borderColor: [189, 195, 199]
  }
};

// Import company logo
const COMPANY_LOGO = new URL('/logo.png', window.location.origin).href;

/**
 * Initializes a new PDF document with common styling and branding
 * @param {string} title - The title of the document
 * @param {string} subtitle - Optional subtitle
 * @returns {Promise<jsPDF>} Configured PDF document
 */
export const initializePDF = async (title, subtitle = '') => {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'pt', // Use points for more precise control
    format: 'letter',
    compress: true, // Enable PDF compression
    putOnlyUsedFonts: true, // Only embed used font subsets
    floatPrecision: 2 // Reduce precision for better compression
  });
  
  try {
    // Load and add logo
    const response = await fetch(COMPANY_LOGO);
    const blob = await response.blob();
    const reader = new FileReader();
    
    await new Promise((resolve, reject) => {
      reader.onload = () => {
        try {
          // Add logo to PDF with compression settings
          const logoData = reader.result.split(',')[1];
          doc.addImage(
            logoData, 
            'PNG', 
            PDF_STYLES.margins.left, 
            PDF_STYLES.margins.top, 
            180, 
            90,
            undefined, 
            'FAST', // Use FAST compression algorithm
            0 // Maximum compression quality (0 = highest compression, 1 = best quality)
          );
          resolve();
        } catch (error) {
          console.error('Error adding logo to PDF:', error);
          resolve();
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error loading logo:', error);
  }

  // Add title - adjusted position to account for larger logo
  doc.setFontSize(PDF_STYLES.fonts.header.size);
  doc.setFont(undefined, PDF_STYLES.fonts.header.style);
  doc.setTextColor(...PDF_STYLES.textColor);
  doc.text(title, doc.internal.pageSize.width - PDF_STYLES.margins.right, PDF_STYLES.margins.top + 40, { align: 'right' });

  // Add subtitle if provided - adjusted position
  if (subtitle) {
    doc.setFontSize(PDF_STYLES.fonts.subheader.size);
    doc.setFont(undefined, 'normal');
    doc.text(subtitle, doc.internal.pageSize.width - PDF_STYLES.margins.right, PDF_STYLES.margins.top + 70, { align: 'right' });
  }

  // Add date - adjusted position
  doc.setFontSize(PDF_STYLES.fonts.body.size);
  doc.setFont(undefined, 'normal');
  doc.text(
    `Generated: ${new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })}`,
    doc.internal.pageSize.width - PDF_STYLES.margins.right,
    PDF_STYLES.margins.top + (subtitle ? 95 : 70),
    { align: 'right' }
  );

  return doc;
};

/**
 * Adds a section header to the PDF
 * @param {jsPDF} doc - The PDF document
 * @param {string} text - The header text
 * @param {number} y - The y position to add the header
 * @returns {number} The new y position after adding the header
 */
export const addSectionHeader = (doc, text, y) => {
  doc.setFontSize(PDF_STYLES.fonts.subheader.size);
  doc.setFont(undefined, PDF_STYLES.fonts.subheader.style);
  doc.setTextColor(...PDF_STYLES.textColor);
  doc.text(text, PDF_STYLES.margins.left, y);
  
  // Add a more prominent section divider
  const lineY = y + 5;
  
  // Draw main line
  doc.setDrawColor(...PDF_STYLES.primaryColor);
  doc.setLineWidth(1);
  doc.line(
    PDF_STYLES.margins.left,
    lineY,
    doc.internal.pageSize.width - PDF_STYLES.margins.right,
    lineY
  );
  
  return y + 25;
};

/**
 * Adds a header divider line to the PDF
 * @param {jsPDF} doc - The PDF document
 * @param {number} y - The y position for the line
 */
export const addHeaderDivider = (doc, y) => {
  // Draw main line
  doc.setDrawColor(...PDF_STYLES.primaryColor);
  doc.setLineWidth(1);
  doc.line(
    PDF_STYLES.margins.left,
    y,
    doc.internal.pageSize.width - PDF_STYLES.margins.right,
    y
  );
};

/**
 * Formats currency values consistently
 * @param {number} amount - The amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

/**
 * Returns common table styles for autoTable
 * @returns {Object} Table styles configuration
 */
export const getTableStyles = () => ({
  theme: 'grid',
  headStyles: {
    fillColor: PDF_STYLES.table.headerBg,
    textColor: PDF_STYLES.table.headerText,
    fontStyle: 'bold',
    fontSize: 10,
    cellPadding: 6,
    lineWidth: 0.5,
    lineColor: PDF_STYLES.table.borderColor
  },
  bodyStyles: {
    textColor: PDF_STYLES.textColor,
    fontSize: 9,
    cellPadding: 5,
    lineColor: PDF_STYLES.table.borderColor,
    lineWidth: 0.5
  },
  alternateRowStyles: {
    fillColor: PDF_STYLES.table.stripedBg
  },
  margin: {
    top: PDF_STYLES.margins.top,
    left: PDF_STYLES.margins.left,
    right: PDF_STYLES.margins.right,
    bottom: PDF_STYLES.margins.bottom
  },
  tableWidth: 'auto',
  styles: {
    overflow: 'linebreak',
    cellWidth: 'wrap',
    fontSize: 9,
    cellPadding: 5,
    valign: 'middle',
    minCellHeight: 10
  },
  willDrawCell: function(data) {
    if (data.cell.text.length === 0 || data.cell.text[0] === '') {
      return false;
    }
    return true;
  },
  didParseCell: function(data) {
    if (data.cell.text && typeof data.cell.text === 'string' && data.cell.text.length > 50) {
      data.cell.text = data.cell.text.substring(0, 50) + '...';
    }
  }
});

export const PDF_STYLES_CONSTANTS = PDF_STYLES;

/**
 * Generate PDF export from calculation results
 * @param {Object} result - Calculation result object
 * @returns {Blob} - PDF file as blob
 */
export const generatePDF = (result) => {
  try {
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(18);
    doc.text('Payment Calculation Results', 105, 15, { align: 'center' });
    
    // Add upload details
    doc.setFontSize(12);
    doc.text(`Upload ID: ${result.uploadId}`, 14, 30);
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 14, 37);
    
    // Add summary table
    doc.autoTable({
      startY: 45,
      head: [['Provider', 'Locations', 'Units', 'Total Cost']],
      body: result.providers.map(provider => [
        provider.name,
        provider.locationCount,
        provider.totalUnits,
        `$${provider.totalCost.toFixed(2)}`
      ]),
      foot: [['Total', result.totalLocations, result.totalUnits, `$${result.totalAmount.toFixed(2)}`]],
      theme: 'grid',
      headStyles: { fillColor: [51, 122, 183] },
      footStyles: { fillColor: [240, 240, 240], fontStyle: 'bold' }
    });
    
    return doc.output('blob');
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

/**
 * Generate a PDF export of projection scenarios
 * @param {string} projectionId - The projection ID
 * @param {string} scenarioId - The scenario ID (optional)
 * @returns {Blob} - PDF file as blob
 */
export const generateProjectionPDF = async (projectionId, scenarioId = null) => {
  try {
    // Fetch projection data
    const { data: projection, error: projectionError } = await supabase
      .from('projections')
      .select('*')
      .eq('id', projectionId)
      .single();
    
    if (projectionError) throw projectionError;
    
    // Fetch scenarios data
    let scenariosQuery = supabase
      .from('projection_scenarios')
      .select('*')
      .eq('projection_id', projectionId);
    
    // If a specific scenario is requested
    if (scenarioId) {
      scenariosQuery = scenariosQuery.eq('id', scenarioId);
    }
    
    const { data: scenarios, error: scenariosError } = await scenariosQuery;
    
    if (scenariosError) throw scenariosError;
    
    // Use the enhanced PDF initialization with logo and styling
    const doc = await initializePDF('Projection & Estimator Results', 
      projection.file_name ? `File: ${projection.file_name.split('_').slice(1).join('_')}` : '');
    
    // Set y position after logo and title
    let yPosition = 130;
    
    // Add base projection summary
    yPosition = addSectionHeader(doc, 'Base Projection Summary', yPosition);
    
    // Get location and unit count data
    const { data: locations, error: locationsError } = await supabase
      .from('projection_locations')
      .select('id, customer_address, logistixai_order_number')
      .eq('projection_id', projectionId);
    
    if (locationsError) {
      console.warn('Error fetching location data for PDF:', locationsError);
    }
    
    // Calculate unique locations and units
    const uniqueLocations = new Set();
    const uniqueUnits = new Set();
    
    if (locations) {
      locations.forEach(location => {
        if (location.customer_address) uniqueLocations.add(location.customer_address.trim());
        if (location.logistixai_order_number) uniqueUnits.add(location.logistixai_order_number);
      });
    }
    
    const locationCount = uniqueLocations.size;
    const unitCount = uniqueUnits.size;
    
    // Base projection summary table
    doc.autoTable({
      startY: yPosition,
      head: [['Metric', 'Value']],
      body: [
        ['Total Locations', locationCount.toString()],
        ['Total Units', unitCount.toString()],
        ['Completion Revenue (Base)', formatCurrency(projection.total_locations * 300)],
        ['Total Revenue', formatCurrency(projection.total_projected_revenue)],
        ['Total Expense', formatCurrency(projection.total_projected_expense)],
        ['Total Gross Profit', formatCurrency(projection.total_projected_margin)]
      ],
      ...getTableStyles(),
      columnStyles: {
        0: { cellWidth: 200 },
        1: { cellWidth: 120 }
      },
      tableWidth: 320
    });
    
    // Add scenarios if available
    if (scenarios && scenarios.length > 0) {
      yPosition = doc.lastAutoTable.finalY + 20;
      yPosition = addSectionHeader(doc, 'Scenarios', yPosition);
      
      // Switch to landscape for scenarios table if more than one scenario
      if (scenarios.length > 1 && !scenarioId) {
        doc.addPage('landscape');
        yPosition = PDF_STYLES.margins.top + 20;
        doc.setFontSize(14);
        doc.text('Scenarios', PDF_STYLES.margins.left, yPosition);
        yPosition += 10;
      }
      
      // For single scenario or specific scenario view, use simplified table
      if (scenarios.length === 1 || scenarioId) {
        const scenario = scenarioId ? scenarios[0] : scenarios[0];
        
        // Detailed scenario table
        doc.autoTable({
          startY: yPosition,
          head: [['Metric', 'Value']],
          body: [
            ['Scenario Name', scenario.name],
            ['Completed Percentage', `${scenario.percent_completed}%`],
            ['Refused Percentage', `${scenario.percent_refused}%`],
            ['Total Locations', scenario.total_locations.toString()],
            ['Completed Locations', Math.round(scenario.total_locations * scenario.percent_completed / 100).toString()],
            ['Refused Locations', Math.round(scenario.total_locations * scenario.percent_refused / 100).toString()],
            ['Revenue Per Completion', '$300.00'],
            ['Revenue Per Refusal', '$50.00'],
            ['Completed Revenue', formatCurrency(scenario.completed_revenue || 0)],
            ['Refusal Revenue', formatCurrency(scenario.refusal_revenue || 0)],
            ['Total Revenue', formatCurrency(scenario.total_revenue)],
            ['Total Expense', formatCurrency(scenario.total_expense)],
            ['Total Gross Profit', formatCurrency(scenario.total_margin)],
            ['Margin Percentage', `${((scenario.total_margin / scenario.total_revenue) * 100).toFixed(2)}%`]
          ],
          ...getTableStyles(),
          columnStyles: {
            0: { cellWidth: 200 },
            1: { cellWidth: 120 }
          },
          tableWidth: 320
        });
      } else {
        // Multiple scenarios - use a wider table with horizontal orientation for headers
        // Define fixed column widths that will fit within landscape page
        const pageWidth = doc.internal.pageSize.width;
        const tableWidth = pageWidth - (PDF_STYLES.margins.left + PDF_STYLES.margins.right);
        
        // Create scenario rows
        const scenarioRows = scenarios.map(scenario => [
          scenario.name,
          `${scenario.percent_completed}%`,
          `${scenario.percent_refused}%`,
          scenario.total_locations.toString(),
          formatCurrency(scenario.completed_revenue || (scenario.total_revenue * scenario.percent_completed / 100)),
          formatCurrency(scenario.refusal_revenue || (scenario.total_revenue * scenario.percent_refused / 100)),
          formatCurrency(scenario.total_revenue),
          formatCurrency(scenario.total_margin)
        ]);

        doc.autoTable({
          startY: yPosition,
          head: [['Scenario Name', 'Completed %', 'Refused %', 'Locations', 'Completed Revenue', 'Refusal Revenue', 'Total Revenue', 'Margin']],
          body: scenarioRows,
          ...getTableStyles(),
          columnStyles: {
            0: { cellWidth: tableWidth * 0.20 },  // Scenario name
            1: { cellWidth: tableWidth * 0.10 },  // Completed %
            2: { cellWidth: tableWidth * 0.10 },  // Refused %
            3: { cellWidth: tableWidth * 0.10 },  // Locations
            4: { cellWidth: tableWidth * 0.125 }, // Completed Revenue
            5: { cellWidth: tableWidth * 0.125 }, // Refusal Revenue
            6: { cellWidth: tableWidth * 0.125 }, // Total Revenue
            7: { cellWidth: tableWidth * 0.125 }  // Margin
          },
          didDrawPage: (data) => {
            // Add header to new pages
            if (data.pageNumber > 1) {
              doc.setFontSize(12);
              doc.text('Projection & Estimator Results - Scenarios (Continued)', 
                pageWidth / 2, PDF_STYLES.margins.top, { align: 'center' });
            }
          }
        });
      }
    }
    
    return doc.output('blob');
  } catch (error) {
    console.error('Error generating projection PDF:', error);
    throw error;
  }
};

/**
 * Export PDF and trigger download
 * @param {Blob} pdfBlob - The PDF blob to download
 * @param {string} fileName - The file name for the download
 */
export const downloadPDF = (pdfBlob, fileName) => {
  try {
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading PDF:', error);
    throw error;
  }
}; 